import { Link, Navigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { getStory, stories } from "@/mocks/stories";
import { products } from "@/mocks/products";
import ProductCard from "@/components/feature/ProductCard";
import SmartImage from "@/components/base/SmartImage";

export default function StoryDetail() {
  const { slug = "" } = useParams();
  const story = getStory(slug);
  if (!story) return <Navigate to="/journal" replace />;

  const related = (story.related || [])
    .map((s) => stories.find((x) => x.slug === s))
    .filter(Boolean) as typeof stories;

  const relatedGear = products.slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-100 flex flex-col">
      <SiteHeader variant="overlay" />
      <main className="flex-1">
        {/* Lead image */}
        <section className="relative w-full h-[440px] md:h-[680px] overflow-hidden bg-background-100">
          <SmartImage src={story.cover} alt={story.title} tone="dark" eager imgClassName="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/10 to-black/55"></div>
          <div className="relative z-10 h-full w-full px-5 md:px-10 pb-12 md:pb-20 flex flex-col justify-end">
            <Link to="/journal" className="text-[10px] tracking-[0.24em] uppercase text-foreground-100/80 hover:text-foreground-50 mb-4 cursor-pointer">
              ← Stories
            </Link>
            <h1 className="text-[30px] md:text-[48px] leading-[1.1] md:leading-[1.05] tracking-[-0.02em] text-foreground-50 font-light max-w-3xl">{story.title}</h1>
            <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-100/80 mt-4">{story.meta}</p>
          </div>
        </section>

        {/* Body */}
        <section className="py-12 md:py-20">
          <article className="max-w-2xl mx-auto px-5 md:px-0">
            <p className="text-[17px] md:text-[18px] text-foreground-200 leading-[1.6] mb-6 font-light">
              {story.excerpt}
            </p>
            <div className="space-y-6 text-[16px] md:text-[15px] text-foreground-300 leading-[1.7]">
              {story.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </article>
        </section>

        {/* Gallery */}
        {story.gallery.length > 0 && (
          <section className="pb-12 md:pb-20">
            <div className="w-full px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {story.gallery.map((g, i) => (
                <div key={i} className="relative w-full h-[240px] md:h-[440px] overflow-hidden bg-background-100">
                  <SmartImage src={g} alt="" tone="dark" imgClassName="w-full h-full object-cover object-center" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related gear */}
        <section className="bg-white text-neutral-900 py-12 md:py-16">
          <div className="w-full px-5 md:px-10">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-[22px] md:text-[26px] leading-[1.1] tracking-[-0.01em] font-light text-neutral-900">Gear from this story.</h2>
              <Link to="/shop" className="text-[12px] text-neutral-700 hover:text-primary-600 border-b border-neutral-300 pb-1 cursor-pointer">Shop all</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 md:gap-x-10 gap-y-8 md:gap-y-12 max-w-[1600px] mx-auto" data-product-shop>
              {relatedGear.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-12 md:py-20">
            <div className="w-full px-5 md:px-10">
              <h2 className="text-[22px] md:text-[26px] leading-[1.1] tracking-[-0.01em] text-foreground-50 font-light mb-8">Keep reading.</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
                {related.map((s) => (
                  <Link key={s.slug} to={`/journal/${s.slug}`} className="group cursor-pointer">
                    <div className="relative w-full aspect-[3/2] md:h-[360px] md:aspect-auto overflow-hidden bg-background-100 mb-4">
                      <SmartImage src={s.cover} alt={s.title} tone="dark" imgClassName="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-[1000ms] ease-out" />
                    </div>
                    <p className="text-[10px] tracking-[0.24em] uppercase text-foreground-400 mb-2">{s.category}</p>
                    <h3 className="text-[20px] md:text-[22px] leading-[1.2] tracking-[-0.01em] text-foreground-50 font-light group-hover:text-primary-400 transition">{s.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}